import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, MapPin, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface EliminationScheduleMatch {
  phase: string;
  court: number;
  startTime: string;
  endTime: string;
  category: string;
  participants: string[];
  date: string;
  status?: 'scheduled' | 'completed';
  score?: string;
  winnerTeam?: number | null;
  assignedCourt?: string;
  matchNumber?: number;
  courtHidden?: boolean; // organizer hid the pre-assigned court from participants
  /** Pase directo: este equipo se saltó la ronda anterior y espera a un ganador. */
  directQualifier?: boolean;
  directSeed?: number;
  /** Plaza que se llena con el ganador de ese partido de la ronda anterior. */
  feedsFromMatch1?: number;
  feedsFromMatch2?: number;
}

interface EliminationBracketTreeProps {
  eliminationMatches: EliminationScheduleMatch[];
  player1Name?: string | null;
  player2Name?: string | null;
  nextUpCourt?: string | null;
  /** Normalized phase key from notification: 'octavos' | 'cuartos' | 'semifinales' | 'final' | 'eliminacion' */
  nextUpPhase?: string | null;
}

type PhaseKey = 'octavos' | 'cuartos' | 'semifinales' | 'final';

const PHASE_MAP: Record<string, PhaseKey> = {
  'Octavos de Final': 'octavos',
  'Cuartos de Final': 'cuartos',
  'Semifinales': 'semifinales',
  'Semifinal': 'semifinales',
  'Final': 'final',
};

const PHASE_ORDER: PhaseKey[] = ['octavos', 'cuartos', 'semifinales', 'final'];

const normalize = (name: string) =>
  name?.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s*[-/]\s*/g, ' / ').replace(/\s+/g, ' ') || '';

function lower_is_group(phase: string): boolean {
  return phase?.toLowerCase().startsWith('grupo') || false;
}

const EliminationBracketTree = ({
  eliminationMatches,
  player1Name,
  player2Name,
  nextUpCourt,
  nextUpPhase,
}: EliminationBracketTreeProps) => {
  const { t, language } = useLanguage();

  const userFullName = useMemo(() => {
    if (!player1Name) return '';
    return player2Name
      ? normalize(`${player1Name} / ${player2Name}`)
      : normalize(player1Name);
  }, [player1Name, player2Name]);

  const phaseGroups = useMemo(() => {
    const groups: Record<PhaseKey, EliminationScheduleMatch[]> = {
      octavos: [], cuartos: [], semifinales: [], final: [],
    };
    eliminationMatches.forEach(match => {
      const phase = match.phase;
      let key = PHASE_MAP[phase];
      if (!key) {
        const lower = phase?.toLowerCase() || '';
        if (lower.includes('octavo') || lower === 'r16' || lower === 'oct') key = 'octavos';
        else if (lower.includes('cuarto') || lower === 'qf') key = 'cuartos';
        else if (lower.includes('semi') || lower === 'sf') key = 'semifinales';
        else if (lower.includes('final')) key = 'final';
      }
      if (key && !lower_is_group(phase)) {
        groups[key].push(match);
      }
    });
    for (const key of PHASE_ORDER) {
      groups[key].sort((a, b) => (a.matchNumber || 0) - (b.matchNumber || 0));
    }
    return groups;
  }, [eliminationMatches]);

  const activePhases = useMemo(() =>
    PHASE_ORDER.filter(p => phaseGroups[p].length > 0),
  [phaseGroups]);

  /**
   * Equipos que se saltaron una ronda. Se deducen de la ronda siguiente: una
   * plaza sin `feedsFromMatch` y con equipo real es un pase directo.
   *
   * Sin esto, el jugador con bye entra a la pestaña de su ronda y no aparece por
   * ningún lado, como si no estuviera en el torneo.
   */
  /**
   * Columna de una ronda: partidos y pases directos, EN SU POSICIÓN del cuadro.
   *
   * Se reconstruye recorriendo la ronda siguiente plaza por plaza —cada plaza
   * viene del ganador de un partido o de un pase directo—, igual que en el
   * organizador. Así el pase directo entra en la llave junto al partido con el
   * que se cruza, en vez de quedar suelto en un bloque aparte.
   */
  const columnaPorFase = useMemo(() => {
    const activas = PHASE_ORDER.filter(p => phaseGroups[p].length > 0);
    const out: Partial<Record<PhaseKey, Array<
      { tipo: 'match'; match: EliminationScheduleMatch } |
      { tipo: 'bye'; team: string; next: PhaseKey } |
      { tipo: 'bye-pendiente'; next: PhaseKey }
    >>> = {};
    const hueco = (v?: string) =>
      !v || ['por definir', 'tbd', 'por asignar'].includes(v.trim().toLowerCase());

    for (let i = 0; i < activas.length; i++) {
      const fase = activas[i];
      const actuales = phaseGroups[fase];
      const siguiente = activas[i + 1];
      const plano: any[] = [];
      let algunBye = false;

      if (siguiente) {
        const porNumero = new Map(actuales.map(m => [m.matchNumber, m]));
        for (const q of phaseGroups[siguiente]) {
          const p = q.participants || [];
          const plazas: Array<[number | undefined, string | undefined]> = [
            [q.feedsFromMatch1, p[0]],
            [q.feedsFromMatch2, p[1]],
          ];
          for (const [viene, equipo] of plazas) {
            if (viene !== undefined) {
              const real = porNumero.get(viene);
              if (real) plano.push({ tipo: 'match', match: real });
            } else if (!hueco(equipo)) {
              algunBye = true;
              plano.push({ tipo: 'bye', team: equipo as string, next: siguiente });
            }
          }
        }
      }

      const reales = plano.filter(x => x.tipo === 'match').length;
      if (algunBye && reales === actuales.length) {
        out[fase] = plano;
        continue;
      }

      // Cuadro aún sin sembrar: no se sabe QUIÉN pasa directo, pero sí cuántos.
      // La ronda siguiente tiene 2 plazas por partido y sólo `actuales.length`
      // las llena un ganador; el resto son pases directos y se reservan. Sin
      // esto la columna sale corta y no cuadra con la del organizador.
      const plazas = siguiente ? phaseGroups[siguiente].length * 2 : 0;
      const byes = plazas - actuales.length;

      if (siguiente && byes > 0 && byes <= plazas) {
        // Uno a cada rama por turnos: la mitad A son los primeros partidos de la
        // ronda siguiente y la B los últimos; como mucho dos por partido.
        const nSig = phaseGroups[siguiente].length;
        const mitad = Math.ceil(nSig / 2);
        const turnos: number[] = [];
        for (let k = 0; k < mitad; k++) {
          turnos.push(k);
          if (mitad + k < nSig) turnos.push(mitad + k);
        }
        const porPartido = new Array(nSig).fill(0);
        let restantes = byes;
        for (let vuelta = 0; vuelta < 2 && restantes > 0; vuelta++) {
          for (const idx of turnos) {
            if (restantes === 0) break;
            porPartido[idx]++;
            restantes--;
          }
        }
        const cola = [...actuales];
        const reservada: any[] = [];
        for (let k = 0; k < nSig; k++) {
          for (let b = 0; b < porPartido[k]; b++) reservada.push({ tipo: 'bye-pendiente', next: siguiente });
          for (let j = porPartido[k]; j < 2 && cola.length > 0; j++) {
            reservada.push({ tipo: 'match', match: cola.shift()! });
          }
        }
        if (reservada.length === plazas) {
          out[fase] = reservada;
          continue;
        }
      }

      out[fase] = actuales.map(m => ({ tipo: 'match' as const, match: m }));
    }
    return out;
  }, [phaseGroups]);

  const [selectedPhase, setSelectedPhase] = useState<PhaseKey | null>(null);

  const currentPhase = selectedPhase && activePhases.includes(selectedPhase)
    ? selectedPhase
    : activePhases[0] || null;

  if (activePhases.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-6">
        {t('tournament.noElimMatchesYet')}
      </div>
    );
  }

  const isUserInMatch = (match: EliminationScheduleMatch) => {
    if (!userFullName) return false;
    return match.participants?.some(p => normalize(p) === userFullName) || false;
  };

  const getPhaseTitle = (key: PhaseKey) => {
    const titles: Record<PhaseKey, Record<string, string>> = {
      octavos: { es: 'Octavos', en: 'R16' },
      cuartos: { es: 'Cuartos', en: 'QF' },
      semifinales: { es: 'Semis', en: 'SF' },
      final: { es: 'Final', en: 'Final' },
    };
    return titles[key][language] || key;
  };

  const getPhaseFullTitle = (key: PhaseKey) => {
    const titles: Record<PhaseKey, Record<string, string>> = {
      octavos: { es: 'Octavos de Final', en: 'Round of 16' },
      cuartos: { es: 'Cuartos de Final', en: 'Quarterfinals' },
      semifinales: { es: 'Semifinales', en: 'Semifinals' },
      final: { es: 'Final', en: 'Final' },
    };
    return titles[key][language] || key;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    if (!y || !m || !d) return dateStr;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return date.toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { day: 'numeric', month: 'short' });
  };

  const userFirstPendingPhase = (() => {
    for (const phaseKey of activePhases) {
      for (const match of phaseGroups[phaseKey]) {
        if (!isUserInMatch(match)) continue;
        const finished = !!(match.score || match.winnerTeam);
        if (!finished) return phaseKey;
      }
    }
    return null;
  })();

  const truncate = (name: string, max: number) => name.length > max ? name.slice(0, max) + '…' : name;

  const formatCourt = (raw: string | null | undefined): string | null => {
    if (!raw) return null;
    const trimmed = String(raw).trim();
    if (!trimmed) return null;
    if (/^cancha\s/i.test(trimmed)) return trimmed;
    if (/^C\d+$/i.test(trimmed)) return `Cancha ${trimmed.slice(1)}`;
    return `Cancha ${trimmed}`;
  };

  const isPlaceholder = (name: string) =>
    !name || ['por definir', 'tbd', 'por asignar'].includes(name.trim().toLowerCase());

  /** Nombre de la ronda anterior a `phaseKey`, si existe en este cuadro. */
  const previousPhaseTitle = (phaseKey: PhaseKey): string | null => {
    const idx = PHASE_ORDER.indexOf(phaseKey);
    for (let i = idx - 1; i >= 0; i--) {
      if (phaseGroups[PHASE_ORDER[i]].length > 0) return getPhaseFullTitle(PHASE_ORDER[i]);
    }
    return null;
  };

  const renderMatchCard = (match: EliminationScheduleMatch, phaseKey: PhaseKey, index: number) => {
    const rawTeam1 = match.participants?.[0] || 'TBD';
    const rawTeam2 = match.participants?.[1] || 'TBD';
    const isFinished = !!(match.score || match.winnerTeam);

    // Pase directo: el rival aún no existe porque sale de la ronda anterior.
    // Decir "Ganador de Octavos" en vez de "Por definir" explica por qué el
    // hueco está vacío y deja claro que el otro equipo no se saltó nada.
    const prevPhase = match.directQualifier ? previousPhaseTitle(phaseKey) : null;
    const pendingLabel = prevPhase
      ? (language === 'es' ? `Ganador de ${prevPhase}` : `Winner of ${prevPhase}`)
      : null;
    const label = (name: string) =>
      pendingLabel && isPlaceholder(name) && !isFinished ? pendingLabel : name;
    const team1 = label(rawTeam1);
    const team2 = label(rawTeam2);
    const userIn = isUserInMatch(match);
    const assignedCourt = formatCourt(match.assignedCourt);
    const formattedNextUpCourt = formatCourt(nextUpCourt);
    // Gate the next-up banner by phase
    const phaseMatchesNotif = !nextUpPhase
      || nextUpPhase === 'eliminacion'
      || nextUpPhase === phaseKey;
    const isUserNextUp = userIn && !isFinished && !!formattedNextUpCourt && phaseMatchesNotif;
    const footerCourt = isUserNextUp && formattedNextUpCourt
      ? formattedNextUpCourt
      : assignedCourt
        ? assignedCourt
        // Pre-assigned court is suppressed when the organizer hid courts.
        // Live assignments (assignedCourt / next-up) above still show.
        : (!match.courtHidden && match.court ? `Cancha ${match.court}` : null);

    const isUserTeam1 = userFullName && normalize(team1) === userFullName;
    const isUserTeam2 = userFullName && normalize(team2) === userFullName;

    const userWon = isFinished && (
      (isUserTeam1 && match.winnerTeam === 1) || (isUserTeam2 && match.winnerTeam === 2)
    );
    const userLost = isFinished && userIn && !userWon;

    const statusText = isFinished
      ? (language === 'es' ? 'Finalizado' : 'Finished')
      : isUserNextUp
        ? (language === 'es' ? `Próximo en ${formattedNextUpCourt}` : `Next on ${formattedNextUpCourt}`)
        : assignedCourt
          ? assignedCourt
          : (language === 'es' ? 'Sin asignar' : 'Unassigned');
    const statusBg = isFinished
      ? 'bg-muted/60 text-muted-foreground'
      : isUserNextUp
        ? 'bg-blue-50 text-blue-700'
        : assignedCourt
          ? 'bg-green-50 text-green-700'
          : 'bg-muted/60 text-muted-foreground';

    return (
      <div
        key={`${phaseKey}-${index}`}
        className={`rounded-xl border overflow-hidden ${
          userIn
            ? 'border-primary/40 ring-1 ring-primary/20'
            : 'border-border'
        } bg-card`}
      >
        {/* Status bar */}
        <div className={`px-3 py-1.5 text-xs font-medium text-center border-b ${statusBg}`}>
          {isFinished ? '✓ ' : isUserNextUp ? <><MapPin className="inline h-3.5 w-3.5 mr-1" /></> : assignedCourt ? <><MapPin className="inline h-3.5 w-3.5 mr-1" /></> : ''}{statusText}
        </div>

        {/* Body */}
        <div className="px-3 py-3 space-y-2">
          {/* Pase directo: sin esto, el jugador con bye no entiende por qué no
              tiene partido en la ronda anterior ni por qué su rival es un hueco. */}
          {match.directQualifier && !isFinished && (
            <div className="flex justify-center">
              <Badge
                variant="outline"
                className="text-[10px] px-2 py-0.5 bg-amber-50 border-amber-300 text-amber-700"
              >
                {userIn
                  ? (language === 'es' ? '⚡ Pasas directo' : '⚡ You advance directly')
                  : (language === 'es' ? '⚡ Pase directo' : '⚡ Bye')}
              </Badge>
            </div>
          )}

          {userIn && isFinished && (
            <div className="flex justify-end">
              <Badge variant="outline" className={`text-[10px] px-2 py-0.5 ${
                userWon ? 'bg-green-50 border-green-300 text-green-700' : 'bg-red-50 border-red-300 text-red-700'
              }`}>
                {userWon
                  ? (language === 'es' ? '✓ Ganaste' : '✓ Won')
                  : (language === 'es' ? '✗ Perdiste' : '✗ Lost')}
              </Badge>
            </div>
          )}

          {/* Team 1 */}
          <p className={`text-sm text-center ${
            isUserTeam1 ? 'font-bold text-primary' :
            isFinished && match.winnerTeam === 1 ? 'font-bold text-foreground' :
            isFinished && match.winnerTeam === 2 ? 'text-muted-foreground line-through' :
            'font-medium text-foreground'
          }`}>
            {team1}
          </p>

          {/* Score or VS */}
          <p className={`text-center font-bold ${isFinished ? 'text-base text-foreground' : 'text-xs text-muted-foreground'}`}>
            {isFinished && match.score ? match.score : 'VS'}
          </p>

          {/* Team 2 */}
          <p className={`text-sm text-center ${
            isUserTeam2 ? 'font-bold text-primary' :
            isFinished && match.winnerTeam === 2 ? 'font-bold text-foreground' :
            isFinished && match.winnerTeam === 1 ? 'text-muted-foreground line-through' :
            'font-medium text-foreground'
          }`}>
            {team2}
          </p>
        </div>

        {/* Footer */}
        {(match.date || match.startTime || footerCourt) && (
          <div className="px-3 py-2 border-t border-border/50 flex items-center justify-center gap-3 text-xs text-muted-foreground">
            {match.date && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(match.date)}
              </span>
            )}
            {match.startTime && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {match.startTime}
              </span>
            )}
            {footerCourt && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {footerCourt}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  // Group matches into pairs
  const currentColumn = currentPhase ? (columnaPorFase[currentPhase] ?? []) : [];
  const pairs: (typeof currentColumn)[] = [];
  for (let i = 0; i < currentColumn.length; i += 2) {
    pairs.push(currentColumn.slice(i, i + 2));
  }

  return (
    <div className="w-full">
      {/* Phase selector pills */}
      <div className="flex gap-2 overflow-x-auto pb-3 px-2 scrollbar-hide">
        {activePhases.map((phaseKey) => (
          <button
            key={phaseKey}
            onClick={() => setSelectedPhase(phaseKey)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors border ${
              currentPhase === phaseKey
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted'
            }`}
          >
            {phaseKey === 'final' && '🏆 '}
            {getPhaseTitle(phaseKey)}
          </button>
        ))}
      </div>

      {/* Phase title */}
      {currentPhase && (
        <h3 className="text-center text-sm font-semibold text-foreground mb-3">
          {currentPhase === 'final' && '🏆 '}
          {getPhaseFullTitle(currentPhase)}
        </h3>
      )}

      {/* Partidos con la llave a la derecha.
          Antes había sólo una línea vertical suelta, que no explicaba nada. La
          llave une los dos partidos que se cruzan y apunta a la ronda siguiente,
          que es justo lo que hacía falta para entender contra quién te toca. */}
      <div className="flex flex-col gap-6 pl-2 pr-3">
        {pairs.map((pair, pairIdx) => (
          <div key={pairIdx} className="flex items-stretch">
            {/* Cards column */}
            <div className="flex-1 flex flex-col gap-1.5 min-w-0">
              {pair.map((entrada, idx) => {
                if (entrada.tipo === 'match') {
                  return renderMatchCard(entrada.match, currentPhase!, pairIdx * 2 + idx);
                }
                // Pase directo: entra en la llave como una casilla más, para que
                // se vea con quién se cruza. Diseño propio, sin marcador ni hora.
                // Si el cuadro aún no está sembrado se sabe cuántos pases directos
                // habrá pero no quiénes: la casilla se reserva igualmente, para
                // que la llave cuadre y coincida con la del organizador.
                const pendiente = entrada.tipo === 'bye-pendiente';
                const nombreBye = pendiente ? '' : entrada.team;
                const esYo = !pendiente && userFullName && normalize(nombreBye) === userFullName;
                return (
                  <div
                    key={`bye-${pairIdx}-${idx}`}
                    className={`rounded-xl border border-dashed px-3 py-3 flex flex-col justify-center ${
                      esYo ? 'border-primary/50 bg-primary/5' : 'border-amber-300 bg-amber-50/70'
                    }`}
                  >
                    <div className="flex justify-center mb-1">
                      <Badge variant="outline" className="text-[10px] px-2 py-0.5 bg-amber-50 border-amber-300 text-amber-700">
                        {esYo
                          ? (language === 'es' ? '⚡ Pasas directo' : '⚡ You advance directly')
                          : (language === 'es' ? '⚡ Pase directo' : '⚡ Bye')}
                      </Badge>
                    </div>
                    <p className={`text-sm text-center ${
                      pendiente ? 'italic text-muted-foreground'
                                : esYo ? 'font-bold text-primary' : 'font-medium text-foreground'
                    }`}>
                      {pendiente ? (language === 'es' ? 'Por definir' : 'TBD') : nombreBye}
                    </p>
                    <p className="text-xs text-center text-muted-foreground mt-0.5">
                      {language === 'es' ? 'Pasa a' : 'Advances to'} {getPhaseFullTitle(entrada.next)}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Llave: brazos a la altura de cada tarjeta, espina que los une y
                salida hacia la ronda siguiente. */}
            {pair.length === 2 && (
              <div className="flex-shrink-0 w-9 relative self-stretch text-muted-foreground/40">
                <div className="absolute left-0 right-[10px] top-1/4 border-t-2 border-current" />
                <div className="absolute left-0 right-[10px] bottom-1/4 border-t-2 border-current" />
                <div className="absolute right-[10px] top-1/4 bottom-1/4 border-r-2 border-current rounded-r-md" />
                <div className="absolute right-0 top-1/2 w-[10px] border-t-2 border-current" />
                <ChevronRight className="h-4 w-4 absolute right-[-6px] top-1/2 -translate-y-1/2 text-muted-foreground/70 bg-background rounded-full" />
              </div>
            )}

            {/* Un único partido en la ronda (p. ej. la Final): sin llave. */}
            {pair.length === 1 && <div className="flex-shrink-0 w-9" />}
          </div>
        ))}
      </div>

      {/* A dónde lleva esta ronda */}
      {currentPhase && (() => {
        const idx = activePhases.indexOf(currentPhase);
        const siguiente = activePhases[idx + 1];
        if (!siguiente) return null;
        return (
          <p className="text-center text-xs text-muted-foreground mt-3">
            {language === 'es'
              ? `Los ganadores avanzan a ${getPhaseFullTitle(siguiente)}`
              : `Winners advance to ${getPhaseFullTitle(siguiente)}`}
          </p>
        );
      })()}
    </div>
  );
};

export default EliminationBracketTree;
